import type { ConditionOperator } from '@reach/shared-types/conditions';
import {
  ConditionOperatorEnumType,
  type BetweenDate,
  type RelativeDate,
} from '@reach/shared-types/conditions';
import { Check } from 'lucide-react';
import React, { useState } from 'react';
import { BetweenDateBuilder } from './BetweenDateBuilder';
import { RelativeDateBuilder } from './RelativeDateBuilder';
import type { BuilderConditionValue, BuilderFieldType } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

interface ValueInputProps {
  fieldType: BuilderFieldType;
  operator: ConditionOperator;
  value: BuilderConditionValue[];
  onChange: (value: BuilderConditionValue[]) => void;
  disabled?: boolean;
  /** When the schema defines an enum for this field, show a dropdown with these options. */
  enumOptions?: string[];
  /** When the value input drives the operator (e.g. equals/between Relative vs Absolute), call this to sync. */
  onOperatorChange?: (operator: ConditionOperator) => void;
  /** Optional: update multiple condition fields in one go (avoids race when switching date variant). */
  onConditionChange?: (updates: {
    operator?: ConditionOperator;
    value?: BuilderConditionValue[];
  }) => void;
}

const VALUE_LESS_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.EXISTS,
  ConditionOperatorEnumType.NOT_EXISTS,
];

/** Operators that use free-text substring input; use text input even when enum is defined. */
const SUBSTRING_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.CONTAINS,
  ConditionOperatorEnumType.NOT_CONTAINS,
];

const BETWEEN_DATE_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.BETWEEN_MONTH_DAY,
  ConditionOperatorEnumType.BETWEEN_MONTH_DAY_YEAR,
];

/** greater_than / less_than for date fields support both relative and absolute values. */
const GREATER_LESS_THAN_DATE_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.GREATER_THAN,
  ConditionOperatorEnumType.LESS_THAN,
];

function atLeastOneEnumStartsWith(enumOptions: string[], prefix: string): boolean {
  if (!prefix.trim()) return true;
  return enumOptions.some(opt => opt.startsWith(prefix));
}

function isRelativeDate(v: BuilderConditionValue): v is RelativeDate {
  return typeof v === 'object' && v !== null && 'type' in v && (v as RelativeDate).type === 'relative';
}

function isBetweenDate(v: BuilderConditionValue): v is BetweenDate {
  return typeof v === 'object' && v !== null && 'startDate' in v && 'endDate' in v;
}

function dateToInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const EQUALS_DATE_OPERATORS: ConditionOperator[] = [
  ConditionOperatorEnumType.EQUALS_MONTH_DAY,
  ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR,
];

/** Equals date: same Relative | Absolute toggle UI as GT/LT. */
function EqualsDateInput({
  operator,
  value,
  onChange,
  onOperatorChange,
  onConditionChange,
  disabled,
}: {
  operator: ConditionOperator;
  value: BuilderConditionValue | undefined;
  onChange: (value: BuilderConditionValue[]) => void;
  onOperatorChange?: (operator: ConditionOperator) => void;
  onConditionChange?: (updates: {
    operator?: ConditionOperator;
    value?: BuilderConditionValue[];
  }) => void;
  disabled?: boolean;
}) {
  const variant = operator === ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR ? 'absolute' : 'relative';

  const handleVariantChange = (v: 'relative' | 'absolute') => {
    const newOperator =
      v === 'absolute'
        ? ConditionOperatorEnumType.EQUALS_MONTH_DAY_YEAR
        : ConditionOperatorEnumType.EQUALS_MONTH_DAY;
    if (onConditionChange) {
      onConditionChange({ operator: newOperator, value: [] });
    } else {
      onOperatorChange?.(newOperator);
      onChange([]);
    }
  };

  if (variant === 'absolute') {
    const dateVal = value instanceof Date ? value : null;
    const str = dateVal != null ? dateToInputValue(dateVal) : '';
    return (
      <div className={styles.dateVariantWrapper}>
        <div className={styles.dateVariantToggle} role="group" aria-label="Date type">
          <button
            type="button"
            className={styles.dateVariantBtn}
            onClick={() => handleVariantChange('relative')}
            disabled={disabled}>
            Relative
          </button>
          <button
            type="button"
            className={styles.dateVariantBtnActive}
            onClick={() => handleVariantChange('absolute')}
            disabled={disabled}>
            Absolute
          </button>
        </div>
        <input
          type="date"
          className={styles.textInput}
          value={str}
          onChange={e => {
            const v = e.target.value;
            if (!v) {
              onChange([]);
              return;
            }
            onChange([new Date(v + 'T12:00:00')]);
          }}
          disabled={disabled}
          aria-label="Date"
        />
      </div>
    );
  }

  const relative = value != null && isRelativeDate(value) ? value : null;
  return (
    <div className={styles.dateVariantWrapper}>
      <div className={styles.dateVariantToggle} role="group" aria-label="Date type">
        <button
          type="button"
          className={styles.dateVariantBtnActive}
          onClick={() => handleVariantChange('relative')}
          disabled={disabled}>
          Relative
        </button>
        <button
          type="button"
          className={styles.dateVariantBtn}
          onClick={() => handleVariantChange('absolute')}
          disabled={disabled}>
          Absolute
        </button>
      </div>
      <RelativeDateBuilder value={relative} onChange={rel => onChange([rel])} disabled={disabled} />
    </div>
  );
}

/**
 * Between date input. BETWEEN_MONTH_DAY (every year) shows only absolute date
 * pickers (year portion is ignored by the backend). BETWEEN_MONTH_DAY_YEAR
 * shows a Relative / Absolute toggle.
 */
function BetweenDateInput({
  operator,
  value,
  onChange,
  disabled,
}: {
  operator: ConditionOperator;
  value: BuilderConditionValue | undefined;
  onChange: (value: BuilderConditionValue[]) => void;
  disabled?: boolean;
}) {
  const betweenVal = value != null && isBetweenDate(value) ? value : null;
  const [variantOverride, setVariantOverride] = React.useState<'relative' | 'absolute' | null>(null);

  const isEveryYear = operator === ConditionOperatorEnumType.BETWEEN_MONTH_DAY;

  if (isEveryYear) {
    return (
      <BetweenDateBuilder
        value={betweenVal}
        onChange={between => onChange([between])}
        disabled={disabled}
        variant="absolute"
        monthDayOnly
      />
    );
  }

  const derivedVariant = betweenVal
    ? betweenVal.startDate instanceof Date
      ? 'absolute'
      : isRelativeDate(betweenVal.startDate)
        ? 'relative'
        : null
    : null;
  const variant = derivedVariant ?? variantOverride ?? 'relative';

  const handleVariantChange = (v: 'relative' | 'absolute') => {
    setVariantOverride(v);
    onChange([]);
  };

  return (
    <div className={styles.dateVariantWrapper}>
      <div className={styles.dateVariantToggle} role="group" aria-label="Date type">
        <button
          type="button"
          className={variant === 'relative' ? styles.dateVariantBtnActive : styles.dateVariantBtn}
          onClick={() => handleVariantChange('relative')}
          disabled={disabled}>
          Relative
        </button>
        <button
          type="button"
          className={variant === 'absolute' ? styles.dateVariantBtnActive : styles.dateVariantBtn}
          onClick={() => handleVariantChange('absolute')}
          disabled={disabled}>
          Absolute
        </button>
      </div>
      <BetweenDateBuilder
        value={betweenVal}
        onChange={between => onChange([between])}
        disabled={disabled}
        variant={variant}
      />
    </div>
  );
}

/** Relative vs absolute date input for greater_than / less_than date operators. */
function GreaterLessThanDateInput({
  value,
  onChange,
  disabled,
}: {
  value: BuilderConditionValue | undefined;
  onChange: (value: BuilderConditionValue[]) => void;
  disabled?: boolean;
}) {
  const derivedVariant =
    value instanceof Date ? 'absolute' : value != null && isRelativeDate(value) ? 'relative' : null;
  const [variantOverride, setVariantOverride] = useState<'relative' | 'absolute' | null>(null);
  const variant = derivedVariant ?? variantOverride ?? 'relative';

  const handleVariantChange = (v: 'relative' | 'absolute') => {
    setVariantOverride(v);
    onChange([]);
  };

  if (variant === 'absolute') {
    const dateVal = value instanceof Date ? value : null;
    const str = dateVal != null ? dateToInputValue(dateVal) : '';
    return (
      <div className={styles.dateVariantWrapper}>
        <div className={styles.dateVariantToggle} role="group" aria-label="Date type">
          <button
            type="button"
            className={styles.dateVariantBtn}
            onClick={() => handleVariantChange('relative')}
            disabled={disabled}>
            Relative
          </button>
          <button
            type="button"
            className={styles.dateVariantBtnActive}
            onClick={() => handleVariantChange('absolute')}
            disabled={disabled}>
            Absolute
          </button>
        </div>
        <input
          type="date"
          className={styles.textInput}
          value={str}
          onChange={e => {
            const v = e.target.value;
            if (!v) {
              onChange([]);
              return;
            }
            onChange([new Date(v + 'T12:00:00')]);
          }}
          disabled={disabled}
          aria-label="Date"
        />
      </div>
    );
  }

  const relative = value != null && isRelativeDate(value) ? value : null;
  return (
    <div className={styles.dateVariantWrapper}>
      <div className={styles.dateVariantToggle} role="group" aria-label="Date type">
        <button
          type="button"
          className={styles.dateVariantBtnActive}
          onClick={() => handleVariantChange('relative')}
          disabled={disabled}>
          Relative
        </button>
        <button
          type="button"
          className={styles.dateVariantBtn}
          onClick={() => handleVariantChange('absolute')}
          disabled={disabled}>
          Absolute
        </button>
      </div>
      <RelativeDateBuilder value={relative} onChange={rel => onChange([rel])} disabled={disabled} />
    </div>
  );
}

/**
 * Polymorphic value input based on field type and operator.
 */
export const ValueInput: React.FC<ValueInputProps> = ({
  fieldType,
  operator,
  value,
  onChange,
  disabled = false,
  enumOptions,
  onOperatorChange,
  onConditionChange,
}) => {
  if (VALUE_LESS_OPERATORS.includes(operator)) {
    return <span className={styles.conditionRowValue} aria-hidden />;
  }

  if (fieldType === 'string' || (fieldType === 'unknown' && enumOptions?.length)) {
    const useEnumDropdown =
      enumOptions && enumOptions.length > 0 && !SUBSTRING_OPERATORS.includes(operator);
    if (useEnumDropdown) {
      if (operator === 'in') {
        const selected = Array.isArray(value) ? (value as string[]) : [];
        const toggleOption = (opt: string) => {
          if (disabled) return;
          const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
          onChange(next);
        };
        return (
          <div
            className={styles.enumCheckboxList}
            role="listbox"
            aria-label="Values (select multiple)"
            aria-multiselectable>
            {enumOptions.map(opt => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={styles.enumCheckboxOption}
                  onClick={() => toggleOption(opt)}
                  disabled={disabled}>
                  <span
                    className={isSelected ? styles.enumCheckboxIconChecked : styles.enumCheckboxIcon}
                    aria-hidden>
                    {isSelected ? <Check size={14} strokeWidth={2.5} /> : null}
                  </span>
                  <span className={styles.enumCheckboxLabel}>{opt}</span>
                </button>
              );
            })}
          </div>
        );
      }
      const strVal = (value?.[0] as string | undefined) ?? '';
      return (
        <select
          className={styles.selectInput}
          value={strVal}
          onChange={e => onChange([e.target.value])}
          disabled={disabled}
          aria-label="Value">
          <option value="">Select...</option>
          {enumOptions.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    if (fieldType !== 'string') {
      // unknown without enum: fall through to fallback at end
    } else {
      if (SUBSTRING_OPERATORS.includes(operator) && enumOptions && enumOptions.length > 0) {
        const strVal = (value?.[0] as string | undefined) ?? '';
        const invalidPrefix = strVal.trim() !== '' && !atLeastOneEnumStartsWith(enumOptions, strVal);
        return (
          <div className={styles.conditionRowValue}>
            <input
              type="text"
              className={invalidPrefix ? `${styles.textInput} ${styles.inputInvalid}` : styles.textInput}
              value={strVal}
              onChange={e => onChange([e.target.value])}
              disabled={disabled}
              aria-label="Value"
              aria-invalid={invalidPrefix}
            />
            {invalidPrefix && (
              <span className={styles.validationMessage} role="alert">
                No enum value starts with this prefix
              </span>
            )}
          </div>
        );
      }
      if (operator === 'in') {
        const str = Array.isArray(value) && value.length > 0 ? (value as string[]).join(', ') : '';
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          const items = raw
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
          onChange(items);
        };
        return (
          <input
            type="text"
            className={styles.textInput}
            value={str}
            onChange={handleChange}
            placeholder="value1, value2, ..."
            disabled={disabled}
            aria-label="Values (comma-separated)"
          />
        );
      }
      const strVal = (value?.[0] as string | undefined) ?? '';
      return (
        <input
          type="text"
          className={styles.textInput}
          value={strVal}
          onChange={e => onChange([e.target.value])}
          disabled={disabled}
          aria-label="Value"
        />
      );
    }
  }

  if (fieldType === 'number') {
    const numVal = value?.[0];
    const str = typeof numVal === 'number' ? String(numVal) : numVal != null ? String(numVal) : '';
    return (
      <input
        type="number"
        className={styles.textInput}
        value={str}
        onChange={e => {
          const v = e.target.value;
          if (v === '') {
            onChange([]);
            return;
          }
          const n = Number(v);
          if (!Number.isNaN(n)) onChange([n]);
        }}
        disabled={disabled}
        aria-label="Value"
      />
    );
  }

  if (fieldType === 'boolean') {
    const boolVal = value?.[0];
    const selected = boolVal === true ? 'true' : boolVal === false ? 'false' : '';
    return (
      <select
        className={styles.selectInput}
        value={selected}
        onChange={e => {
          const v = e.target.value;
          if (v === 'true') onChange([true]);
          else if (v === 'false') onChange([false]);
          else onChange([]);
        }}
        disabled={disabled}
        aria-label="Value">
        <option value="">Select...</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (fieldType === 'date') {
    const first = value?.[0];
    if (BETWEEN_DATE_OPERATORS.includes(operator)) {
      return (
        <BetweenDateInput operator={operator} value={first} onChange={onChange} disabled={disabled} />
      );
    }
    if (EQUALS_DATE_OPERATORS.includes(operator)) {
      return (
        <EqualsDateInput
          operator={operator}
          value={first}
          onChange={onChange}
          onOperatorChange={onOperatorChange}
          onConditionChange={onConditionChange}
          disabled={disabled}
        />
      );
    }
    if (GREATER_LESS_THAN_DATE_OPERATORS.includes(operator)) {
      return <GreaterLessThanDateInput value={first} onChange={onChange} disabled={disabled} />;
    }
    const relative = isRelativeDate(first) ? first : null;
    return (
      <RelativeDateBuilder value={relative} onChange={rel => onChange([rel])} disabled={disabled} />
    );
  }

  if (fieldType === 'array' && operator === 'array_contains') {
    const strVal = (value?.[0] as string | undefined) ?? '';
    return (
      <input
        type="text"
        className={styles.textInput}
        value={strVal}
        onChange={e => onChange([e.target.value])}
        disabled={disabled}
        aria-label="Value"
      />
    );
  }

  // unknown or fallback: text input
  const strVal = (value?.[0] as string | undefined) ?? '';
  return (
    <input
      type="text"
      className={styles.textInput}
      value={strVal}
      onChange={e => onChange([e.target.value])}
      disabled={disabled}
      aria-label="Value"
    />
  );
};
